import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import API from "../../api/api";
import { useAuth } from "../../contexts/Auth";
import { usePrivy } from "@privy-io/react-auth";
import Button from "../../components/Button";
import { useNavigate } from "react-router-dom";
import {
  PROHIBITED_GEOS,
  PROHIBITED_INDUSTRIES,
  CountryCodeAlpha2,
  USStateCodeAlpha2,
} from "../../utils/constants";
import i18n from "i18n-iso-countries";
import en from "i18n-iso-countries/langs/en.json";

i18n.registerLocale(en);

type Props = {
  isRejected?: boolean;
};

const usStateNames: { [key in USStateCodeAlpha2]: string } = {
  [USStateCodeAlpha2.AL]: "Alabama",
  [USStateCodeAlpha2.AK]: "Alaska",
  [USStateCodeAlpha2.AZ]: "Arizona",
  [USStateCodeAlpha2.AR]: "Arkansas",
  [USStateCodeAlpha2.CA]: "California",
  [USStateCodeAlpha2.CO]: "Colorado",
  [USStateCodeAlpha2.CT]: "Connecticut",
  [USStateCodeAlpha2.DE]: "Delaware",
  [USStateCodeAlpha2.FL]: "Florida",
  [USStateCodeAlpha2.GA]: "Georgia",
  [USStateCodeAlpha2.HI]: "Hawaii",
  [USStateCodeAlpha2.ID]: "Idaho",
  [USStateCodeAlpha2.IL]: "Illinois",
  [USStateCodeAlpha2.IN]: "Indiana",
  [USStateCodeAlpha2.IA]: "Iowa",
  [USStateCodeAlpha2.KS]: "Kansas",
  [USStateCodeAlpha2.KY]: "Kentucky",
  [USStateCodeAlpha2.LA]: "Louisiana",
  [USStateCodeAlpha2.ME]: "Maine",
  [USStateCodeAlpha2.MD]: "Maryland",
  [USStateCodeAlpha2.MA]: "Massachusetts",
  [USStateCodeAlpha2.MI]: "Michigan",
  [USStateCodeAlpha2.MN]: "Minnesota",
  [USStateCodeAlpha2.MS]: "Mississippi",
  [USStateCodeAlpha2.MO]: "Missouri",
  [USStateCodeAlpha2.MT]: "Montana",
  [USStateCodeAlpha2.NE]: "Nebraska",
  [USStateCodeAlpha2.NV]: "Nevada",
  [USStateCodeAlpha2.NH]: "New Hampshire",
  [USStateCodeAlpha2.NJ]: "New Jersey",
  [USStateCodeAlpha2.NM]: "New Mexico",
  [USStateCodeAlpha2.NY]: "New York",
  [USStateCodeAlpha2.NC]: "North Carolina",
  [USStateCodeAlpha2.ND]: "North Dakota",
  [USStateCodeAlpha2.OH]: "Ohio",
  [USStateCodeAlpha2.OK]: "Oklahoma",
  [USStateCodeAlpha2.OR]: "Oregon",
  [USStateCodeAlpha2.PA]: "Pennsylvania",
  [USStateCodeAlpha2.RI]: "Rhode Island",
  [USStateCodeAlpha2.SC]: "South Carolina",
  [USStateCodeAlpha2.SD]: "South Dakota",
  [USStateCodeAlpha2.TN]: "Tennessee",
  [USStateCodeAlpha2.TX]: "Texas",
  [USStateCodeAlpha2.UT]: "Utah",
  [USStateCodeAlpha2.VT]: "Vermont",
  [USStateCodeAlpha2.VA]: "Virginia",
  [USStateCodeAlpha2.WA]: "Washington",
  [USStateCodeAlpha2.WV]: "West Virginia",
  [USStateCodeAlpha2.WI]: "Wisconsin",
  [USStateCodeAlpha2.WY]: "Wyoming",
  [USStateCodeAlpha2.DC]: "District of Columbia",
  [USStateCodeAlpha2.AS]: "American Samoa",
  [USStateCodeAlpha2.GU]: "Guam",
  [USStateCodeAlpha2.MP]: "Northern Mariana Islands",
  [USStateCodeAlpha2.PR]: "Puerto Rico",
  [USStateCodeAlpha2.VI]: "U.S. Virgin Islands",
  [USStateCodeAlpha2.UM]: "U.S. Minor Outlying Islands",
};

export default function UnderReview({ isRejected = false }: Props) {
  const auth = useAuth();
  const { logout } = usePrivy();
  const navigate = useNavigate();
  const [businessInfo, setBusinessInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const getBusinessInfo = async () => {
      if (auth.currentUser?.id) {
        try {
          setLoading(true);
          const { data } = await API.get(
            `business-lead?email=${encodeURIComponent(auth.currentUser.email)}`,
          );
          setBusinessInfo(data);
          setLoading(false);
        } catch (error) {
          console.error("Error fetching business info:", error);
          setLoading(false);
        }
      }
    };

    getBusinessInfo();
  }, [auth.currentUser?.id, auth.currentUser?.email]);

  const handleBackToHome = async () => {
    if (isRejected) {
      try {
        setLoading(true);
        // Log the user out
        await logout();
        setLoading(false);
      } catch (error) {
        console.error("Error logging out:", error);
        setLoading(false);
      }
    }
    // Always navigate to home
    navigate("/");
  };

  const handleContactSupport = () => {
    window.open("mailto:support@getbeam.cash", "_blank");
  };

  return (
    <div className="min-h-screen flex flex-col items-center pt-12 px-6 relative">
      <div className="w-full max-w-2xl bg-white rounded-lg shadow-sm p-8 text-center">
        {isRejected ? (
          // Rejected flow (Group 4)
          <>
            <div className="flex justify-center mb-6">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="text-red-500 text-4xl"
              />
            </div>
            <h2 className="text-2xl font-medium mb-4">
              We're Unable to Proceed
            </h2>
            <p className="text-gray-600 mb-6">
              We appreciate your interest in Beam, but we're unable to onboard
              your business at this time. This may be due to your business type
              or location being outside our current service area.
            </p>

            {businessInfo && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700">
                  {PROHIBITED_INDUSTRIES.includes(businessInfo.industry)
                    ? `Your business industry (${businessInfo.industry}) is currently not supported by our platform.`
                    : businessInfo.hqCountry &&
                        PROHIBITED_GEOS.countries.includes(
                          businessInfo.hqCountry as CountryCodeAlpha2,
                        )
                      ? `Your business location (${
                          i18n.getName(businessInfo.hqCountry, "en") ||
                          businessInfo.hqCountry
                        }) is currently outside our service area.`
                      : businessInfo.hqCountry === "US" &&
                          businessInfo.hqState &&
                          PROHIBITED_GEOS.states.includes(
                            businessInfo.hqState as USStateCodeAlpha2,
                          )
                        ? `Your business location (${
                            usStateNames[
                              businessInfo.hqState as USStateCodeAlpha2
                            ]
                          }, United States) is currently outside our service area.`
                        : `Your business type or location is currently not supported by our platform.`}
                </p>
              </div>
            )}

            <div className="flex flex-col space-y-4">
              <Button
                text="Contact Support"
                onSubmit={handleContactSupport}
                loading={loading}
              />
              <Button
                text="Return to Home"
                onSubmit={handleBackToHome}
                loading={loading}
                className="bg-gray-100 text-lavender-700 border border-lavender-400 hover:bg-gray-200"
              />
            </div>
          </>
        ) : (
          // Regular under review flow
          <>
            <h2 className="text-2xl font-medium mb-4">
              Your Application is Under Review
            </h2>
            <p className="text-gray-600 mb-6">
              Thank you for completing your business verification. Our team is
              currently reviewing your information. This process typically takes
              1-2 business days.
            </p>
            <div className="p-4 bg-blue-50 rounded-lg mb-6">
              <p className="text-blue-700 text-sm">
                You'll receive an email notification once your verification is
                complete, and you can return to this page to continue setting up
                your account.
              </p>
            </div>
            <Button
              text="Return to Accounts"
              onSubmit={() => navigate("/accounts")}
              loading={loading}
            />
          </>
        )}
      </div>
    </div>
  );
}
